import { index } from "~/api/contractEntitySpend.js";
import * as types from "../mutation-types";
import store from "../../store";
import {
  BALANCE_PERIOD_DAY,
  BALANCE_PERIOD_WEEK,
  BALANCE_PERIOD_MONTH,
  BALANCE_PERIOD_ALL
} from "~/constants/contract/balance/period";

function resolveEntitySpendPayload(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  return [];
}

export const state = {
  dataByPeriod: {},
  isLoadingByPeriod: {},
  updatedAtByPeriod: {}
};

const entitySpendRequestMap = new Map();

function normalizePeriodId(periodId) {
  const numericPeriodId = Number(periodId);

  switch (numericPeriodId) {
    case BALANCE_PERIOD_DAY:
    case BALANCE_PERIOD_WEEK:
    case BALANCE_PERIOD_MONTH:
    case BALANCE_PERIOD_ALL:
      return numericPeriodId;
    default:
      return BALANCE_PERIOD_ALL;
  }
}

function createRequestKey(contractId, periodId) {
  return `${contractId}:${periodId}`;
}

export const mutations = {
  [types.CLEAR_CONTRACT_ENTITY_SPEND_CACHE](state) {
    state.dataByPeriod = {};
    state.isLoadingByPeriod = {};
    state.updatedAtByPeriod = {};
  },
  [types.SET_IS_LOADING_CONTRACT_ENTITY_SPEND](state, payload) {
    const periodId = normalizePeriodId(payload?.periodId);
    state.isLoadingByPeriod = {
      ...state.isLoadingByPeriod,
      [periodId]: Boolean(payload?.status)
    };
  },
  [types.SET_CONTRACT_ENTITY_SPEND_DATA](state, payload) {
    const periodId = normalizePeriodId(payload?.periodId);
    state.dataByPeriod = {
      ...state.dataByPeriod,
      [periodId]: Array.isArray(payload?.data) ? payload.data : []
    };
  },
  [types.SET_CONTRACT_ENTITY_SPEND_UPDATED_AT](state, payload) {
    const periodId = normalizePeriodId(payload?.periodId);
    state.updatedAtByPeriod = {
      ...state.updatedAtByPeriod,
      [periodId]: payload?.timestamp || null
    };
  }
};

export const actions = {
  clearContractEntitySpendCache({ commit }) {
    entitySpendRequestMap.clear();
    commit(types.CLEAR_CONTRACT_ENTITY_SPEND_CACHE);
  },

  async fetchContractEntitySpend({ commit }, payload = null) {
    const requestContractId =
      typeof payload === "object" && payload !== null
        ? payload.contractId
        : payload;
    const requestPeriodId =
      typeof payload === "object" && payload !== null
        ? payload.periodId
        : null;

    const activeContractId =
      requestContractId || store.getters["contract/activeContract"]?.id;
    const periodId = normalizePeriodId(requestPeriodId);
    if (!activeContractId) {
      commit(types.SET_CONTRACT_ENTITY_SPEND_DATA, {
        periodId,
        data: []
      });
      commit(types.SET_IS_LOADING_CONTRACT_ENTITY_SPEND, {
        periodId,
        status: false
      });
      return null;
    }

    const requestKey = createRequestKey(activeContractId, periodId);
    if (entitySpendRequestMap.has(requestKey)) {
      return entitySpendRequestMap.get(requestKey);
    }

    commit(types.SET_IS_LOADING_CONTRACT_ENTITY_SPEND, {
      periodId,
      status: true
    });

    const requestPromise = index(activeContractId, periodId)
      .then((response) => {
        const currentActiveContractId =
          store.getters["contract/activeContract"]?.id || null;

        if (
          currentActiveContractId &&
          String(currentActiveContractId) !== String(activeContractId)
        ) {
          return response;
        }

        commit(types.SET_CONTRACT_ENTITY_SPEND_DATA, {
          periodId,
          data: resolveEntitySpendPayload(response?.data)
        });
        commit(types.SET_CONTRACT_ENTITY_SPEND_UPDATED_AT, {
          periodId,
          timestamp: Date.now()
        });
        return response;
      })
      .finally(() => {
        commit(types.SET_IS_LOADING_CONTRACT_ENTITY_SPEND, {
          periodId,
          status: false
        });
        entitySpendRequestMap.delete(requestKey);
      });

    entitySpendRequestMap.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      throw error;
    }
  }
};

export const getters = {
  data: (state) => state.dataByPeriod[BALANCE_PERIOD_ALL] || [],
  list: (state) => state.dataByPeriod[BALANCE_PERIOD_ALL] || [],
  getByPeriod: (state) => (periodId = BALANCE_PERIOD_ALL) => {
    const resolvedPeriodId = normalizePeriodId(periodId);
    return state.dataByPeriod[resolvedPeriodId] || [];
  },
  isLoading: (state) => Boolean(state.isLoadingByPeriod[BALANCE_PERIOD_ALL]),
  isLoadingByPeriod: (state) => (periodId = BALANCE_PERIOD_ALL) => {
    const resolvedPeriodId = normalizePeriodId(periodId);
    return Boolean(state.isLoadingByPeriod[resolvedPeriodId]);
  },
  updatedAt: (state) => state.updatedAtByPeriod[BALANCE_PERIOD_ALL] || null,
  updatedAtByPeriod: (state) => (periodId = BALANCE_PERIOD_ALL) => {
    const resolvedPeriodId = normalizePeriodId(periodId);
    return state.updatedAtByPeriod[resolvedPeriodId] || null;
  }
};
